/**
 * WHERE YOU ARE, ON EVERY SCREEN. D-EZ-73 (F5, F6).
 *
 * From the design review, 20 Aug 2026:
 *
 *   "The race screen knows it's turn 22 of 50. The career page knows it's
 *    season 1. The record book knows which season it's showing. None of them
 *    tells you the other two, so orienting means changing screens to find out
 *    where you were."
 *
 * This is the one line that answers all three at once, and it is the second
 * thing on every page:
 *
 *     Blue Ridge Cup Series · Season 2 · Race 12 of 36 · next Bristol
 *
 * ⚠ IT REPLACED TWO STANDING ROWS RATHER THAN ADDING A THIRD. The account bar
 * and the model panel were both permanently on screen — a form and a heading,
 * for two settings a player touches once — and they are now behind labelled
 * buttons on the right of this strip. Nothing was hidden: a control with a word
 * on it in permanent chrome is findable, which is the whole test the model
 * panel's own note sets itself ("a panel nobody can find is a feature nobody
 * has"). What is gone is the standing furniture.
 *
 * ⚠ AND THE SEASON PICKER LIVES HERE, NOT IN THE RECORD BOOK. That was F6: the
 * chips were built for the record book because that is where Howard hit the
 * wall, but "which season am I looking at" is asked on the career page and the
 * driver card too. One control, every screen, always the same gesture — and it
 * opens the book at the season you picked.
 *
 * Presentational. It computes nothing and decides nothing; the shell hands it
 * what is true and it prints it.
 */

import type { ReactNode } from 'react';

export interface SeasonStripProps {
  leagueName: string;
  /** The season on the workbench, live or settled. Null before there is one. */
  seasonNumber: number | null;
  /** Races in the books this season, and how many there are. */
  racesRun: number | null;
  racesInSeason: number;
  /** The next race's name, when the season has one to come. */
  nextRace: string | null;
  /** True while a race is on the tape, so the strip says so instead of guessing. */
  watching: boolean;
  /** Every settled season a chip can open, ascending. */
  seasons: readonly number[];
  /** Which one the record book would open at. */
  openSeason: number | null;
  onPickSeason: (seasonNumber: number) => void;
  /** The disclosure buttons — account, model — supplied by the shell. */
  children?: ReactNode;
}

export default function SeasonStrip({
  leagueName,
  seasonNumber,
  racesRun,
  racesInSeason,
  nextRace,
  watching,
  seasons,
  openSeason,
  onPickSeason,
  children,
}: SeasonStripProps) {
  /*
    ⚠ AN HONEST EMPTY STRIP, not a hidden one. Before a league exists this says
    so in one short phrase rather than vanishing — a bar that appears and
    disappears is a bar a player cannot learn the position of, and the KEEP list
    in the review is explicit that an empty state tells the truth.
  */
  const started = seasonNumber !== null;

  return (
    <div className="sstrip" aria-label="Where you are">
      <div className="sstrip__where">
        {started ? (
          <>
            <b className="sstrip__league">{leagueName || 'Your league'}</b>
            <span className="sstrip__sep" aria-hidden="true">·</span>
            <span>Season {seasonNumber}</span>
            {racesRun !== null && (
              <>
                <span className="sstrip__sep" aria-hidden="true">·</span>
                <span>
                  {racesRun} of {racesInSeason} races run
                </span>
              </>
            )}
            {nextRace && (
              <>
                <span className="sstrip__sep" aria-hidden="true">·</span>
                <span className="sstrip__next">
                  {watching ? 'now racing:' : 'next'} {nextRace}
                </span>
              </>
            )}
          </>
        ) : (
          <span className="sstrip__none">No league yet — start one on Home and this line follows you around.</span>
        )}
      </div>

      {/*
        F6 — THE SEASON PICKER, ON EVERY SCREEN.

        Drawn only when there is more than one season to choose between: a
        picker with one option is furniture, which is the same rule the record
        book's own shelf already follows.
      */}
      {seasons.length > 1 && (
        <nav className="sstrip__seasons" aria-label="Open a season in the record book">
          {seasons.map((n) => (
            <button
              key={n}
              type="button"
              className={`sstrip__season${n === openSeason ? ' is-on' : ''}`}
              aria-current={n === openSeason ? 'true' : undefined}
              onClick={() => onPickSeason(n)}
              title={`Open season ${n} in the record book`}
            >
              S{n}
            </button>
          ))}
        </nav>
      )}

      <div className="sstrip__tools">{children}</div>
    </div>
  );
}
